import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CustomerOrderDetail,
  PaymentIntentView,
  PaymentMethod,
  PaymentSession,
} from '@capsico/contracts';
import { errorMessage, errorRef } from '../api/client';
import { abandonOrder, confirmIntent, fetchIntent, retryPayment } from '../api/payments';
import { openRazorpay, postForm } from '../utils/gateway';
import { showToast } from './useToast';

export interface PaymentMessages {
  paid: string;
  notFinished: string;
  notYet: string;
  failed: string;
  cancelled: string;
  copied: string;
  gatewayDown: string;
}

type QrSession = Extract<PaymentSession, { kind: 'qr' }>;

export interface QrState {
  intentId: string;
  payload: string;
  amountMinor: number;
}

const QR_POLL_MS = 3000;

/**
 * Paying for one order online (W11): hands the customer to the gateway the server picked
 * (Razorpay sheet, signed form post, redirect or Fonepay QR), passes whatever comes back to the
 * server to verify, retries with another method and cancels an unpaid order. The server is the
 * only judge of "paid"; this hook never marks anything paid on its own.
 */
export function usePayment(
  orderId: string,
  messages: PaymentMessages,
  options: { onPaid?: (intent: PaymentIntentView) => void; onCancelled?: () => void } = {},
) {
  const client = useQueryClient();
  const [qr, setQr] = useState<QrState | null>(null);
  const [opening, setOpening] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const onPaid = useRef(options.onPaid);
  onPaid.current = options.onPaid;
  const onCancelled = useRef(options.onCancelled);
  onCancelled.current = options.onCancelled;
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  const refreshOrder = useCallback(() => {
    void client.invalidateQueries({ queryKey: ['my-order', orderId] });
    void client.invalidateQueries({ queryKey: ['my-orders'] });
  }, [client, orderId]);

  /** What happened, as the server sees it. */
  const settle = useCallback(
    (intent: PaymentIntentView) => {
      client.setQueryData(['payment-intent', intent.id], intent);
      refreshOrder();
      const text = messagesRef.current;
      if (intent.status === 'paid') {
        void client.invalidateQueries({ queryKey: ['wallet'] });
        showToast(text.paid, { tone: 'positive' });
        onPaid.current?.(intent);
      } else if (intent.status === 'pending') {
        showToast(text.notYet);
      } else {
        showToast(intent.failure ?? text.failed, { tone: 'negative' });
      }
    },
    [client, refreshOrder],
  );

  const confirm = useMutation({
    mutationFn: (input: { intentId: string; params: Record<string, string> }) =>
      confirmIntent(input.intentId, input.params),
    onSuccess: (intent) => settle(intent),
    onError: (failure) =>
      showToast(errorMessage(failure), { tone: 'negative', ref: errorRef(failure) }),
  });

  /** Hands the customer to the gateway for this attempt. */
  const start = useCallback(
    (intent: PaymentIntentView) => {
      const text = messagesRef.current;
      if (intent.status !== 'pending') {
        settle(intent);
        return;
      }
      const session = intent.session;
      if (!session) {
        refreshOrder();
        showToast(text.notFinished);
        return;
      }
      switch (session.kind) {
        case 'razorpay_checkout':
          setOpening(true);
          openRazorpay(session)
            .then((params) => {
              if (params) confirm.mutate({ intentId: intent.id, params });
              else {
                refreshOrder();
                showToast(text.notFinished);
              }
            })
            .catch(() => showToast(text.gatewayDown, { tone: 'negative' }))
            .finally(() => setOpening(false));
          return;
        case 'form_post':
          setOpening(true);
          postForm(session.action, session.fields);
          return;
        case 'redirect':
          setOpening(true);
          window.location.assign(session.url);
          return;
        case 'qr': {
          const qrSession: QrSession = session;
          setQr({
            intentId: intent.id,
            payload: qrSession.payload,
            amountMinor: qrSession.amountMinor,
          });
          return;
        }
      }
    },
    [confirm, refreshOrder, settle],
  );

  // Fonepay QR: ask the server every 3 s until the money lands, fails or the attempt expires.
  const qrQuery = useQuery({
    queryKey: ['payment-intent', qr?.intentId ?? 'none'],
    queryFn: () => fetchIntent(qr?.intentId ?? ''),
    enabled: qr !== null,
    refetchInterval: (current) =>
      !current.state.data || current.state.data.status === 'pending' ? QR_POLL_MS : false,
    refetchOnWindowFocus: true,
    staleTime: 0,
  });
  const qrStatus = qrQuery.data?.status;
  useEffect(() => {
    if (!qr || !qrQuery.data || qrQuery.data.id !== qr.intentId) return;
    if (qrStatus && qrStatus !== 'pending') {
      setQr(null);
      settle(qrQuery.data);
    }
  }, [qr, qrQuery.data, qrStatus, settle]);

  const retry = useMutation({
    mutationFn: (method: PaymentMethod) => retryPayment(orderId, method),
    onSuccess: (intent) => {
      setPickerOpen(false);
      refreshOrder();
      start(intent);
    },
    onError: (failure) =>
      showToast(errorMessage(failure), { tone: 'negative', ref: errorRef(failure) }),
  });

  // Cancelling an unpaid order shows at once and is rolled back if the server refuses.
  const abandon = useMutation({
    mutationFn: () => abandonOrder(orderId),
    onMutate: async () => {
      setConfirmCancel(false);
      await client.cancelQueries({ queryKey: ['my-order', orderId] });
      const previous = client.getQueryData<CustomerOrderDetail>(['my-order', orderId]);
      if (previous) {
        client.setQueryData<CustomerOrderDetail>(['my-order', orderId], {
          ...previous,
          stage: 'cancelled',
          payment: { ...previous.payment, intent: null, retryMethods: [] },
        });
      }
      return { previous };
    },
    onError: (failure, _input, context) => {
      if (context?.previous) client.setQueryData(['my-order', orderId], context.previous);
      showToast(errorMessage(failure), { tone: 'negative', ref: errorRef(failure) });
    },
    onSuccess: () => {
      setQr(null);
      showToast(messagesRef.current.cancelled);
      onCancelled.current?.();
    },
    onSettled: () => {
      refreshOrder();
      void client.invalidateQueries({ queryKey: ['wallet'] });
    },
  });

  return {
    /** A gateway hand-off or check is running. */
    busy: opening || confirm.isPending || retry.isPending,
    start,
    /* other methods */
    pickerOpen,
    openPicker: () => setPickerOpen(true),
    closePicker: () => setPickerOpen(false),
    retry: (method: PaymentMethod) => retry.mutate(method),
    retrying: retry.isPending,
    retryingMethod: retry.isPending ? (retry.variables ?? null) : null,
    /* cancel */
    confirmCancel,
    askCancel: () => setConfirmCancel(true),
    keepOrder: () => setConfirmCancel(false),
    abandon: () => abandon.mutate(),
    abandoning: abandon.isPending,
    /* Fonepay QR */
    qr,
    closeQr: () => setQr(null),
    checkQr: () => {
      if (qr) confirm.mutate({ intentId: qr.intentId, params: {} });
    },
    checking: confirm.isPending,
    copyQr: () => {
      if (!qr) return;
      void navigator.clipboard
        ?.writeText(qr.payload)
        .then(() => showToast(messagesRef.current.copied))
        .catch(() => undefined);
    },
  };
}
